"use client";

import React from "react";
import { Clock, MapPin, PhoneCall, ShieldAlert, Navigation, CircleCheck as CheckCircle2, Wrench, Building2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyCorridors = [{
  name: "Southlake Pkwy & Mall Corridor",
  description: "Rapid response to retail centers, apartment communities, and surrounding neighborhoods."
}, {
  name: "Jonesboro Rd Commercial & Residential",
  description: "Direct service route connecting central Morrow directly to our Jonesboro dispatch hub."
}, {
  name: "I-75 Logistics & Hub Corridor",
  description: "Immediate emergency access along Exits 231 and 233 for fast turnaround times."
}];
const highlights = [{
  title: "20-30 Min Rapid Arrival",
  detail: "Direct dispatch trucks pre-staged for Morrow emergencies."
}, {
  title: "Zip Codes 30260 & 30236",
  detail: "Complete coverage across north and south Morrow neighborhoods."
}, {
  title: "24/7 Dedicated Local Crews",
  detail: "Fully licensed plumbers ready for burst pipes, sewer backups, and leaks."
}];
export default function Morrow() {
  const shouldReduceMotion = useReducedMotion();
  const motionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="morrowserviceareaintro" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {/* Top Header & Intro Badge */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 gap-1.5 text-xs font-semibold">
            <MapPin className="size-3.5 text-primary" />
            Morrow, GA Service Hub
          </Badge>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Emergency Plumbing Response in Morrow, Georgia
          </h2>

          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            When urgent water leaks, clogged main drains, or water heater failures strike your Morrow home or business, 
            Jonesboro Emergency Plumber delivers prompt, localized service with verified 20 to 30-minute average response times.
          </p>
        </div>

        {/* Main Grid: Details + Map/Visual Card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Local Familiarity & Key Roads */}
          <div className="lg:col-span-7 space-y-6">
            <Card className="border border-border bg-card shadow-sm">
              <CardHeader>
                <div className="flex items-center gap-2 text-primary">
                  <Navigation className="size-5" />
                  <span className="text-xs font-bold uppercase tracking-wider">Fast Transit Corridors</span>
                </div>
                <CardTitle className="text-2xl font-bold font-default text-card-foreground">
                  Familiar With Every Morrow Neighborhood
                </CardTitle>
                <CardDescription>
                  Our technicians operate daily throughout Clayton County, navigating high-traffic zones smoothly to ensure immediate service.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {keyCorridors.map((corridor, i) => <div key={corridor.name} data-index={i} className="p-4 rounded-lg bg-muted/40 border border-border/60 transition-colors hover:bg-muted/70">
                    <div className="flex items-start gap-3">
                      <div className="p-1.5 rounded-md bg-primary/10 text-primary mt-0.5">
                        <Building2 className="size-4" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm md:text-base text-foreground font-default">
                          {corridor.name}
                        </h3>
                        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
                          {corridor.description}
                        </p>
                      </div>
                    </div>
                  </div>)}

                <Separator className="my-4" />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>Clayton State University Area</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>Southlake Woods & Estates</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>Morrow Industrial Park</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                    <span>Reynolds Nature Preserve Area</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats Banner */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {highlights.map((item, i) => <div key={item.title} data-index={i} className="p-4 rounded-lg border border-border bg-card shadow-xs flex flex-col justify-between">
                  <p className="font-bold text-sm text-primary font-default">
                    {item.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {item.detail}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Right Column: Dispatch Panel & Emergency Action */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border border-border bg-card shadow-md">
              <div className="p-6 md:p-8 space-y-6">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-primary/30 text-primary font-semibold">
                    Live Dispatch Available
                  </Badge>
                  <span className="flex h-2.5 w-2.5 rounded-full bg-chart-4 animate-pulse" />
                </div>

                <div>
                  <h3 className="text-xl md:text-2xl font-bold font-default text-card-foreground">
                    Urgent Plumbing Issue in Morrow?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Burst pipes, backed-up sewers, and water heater leaks require fast action before water causes structural damage.
                  </p>
                </div>

                <div className="space-y-3 rounded-lg bg-muted/50 p-4 border border-border/80 text-xs md:text-sm">
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Active Response Zones:</span>
                    <span className="font-semibold text-foreground">Zip 30260, 30236</span>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Average Travel Time:</span>
                    <span className="font-semibold text-foreground">20 - 30 Minutes</span>
                  </div>
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Dispatch Readiness:</span>
                    <span className="font-semibold text-chart-4">24 Hours / 7 Days</span>
                  </div>
                </div>

                {/* Dispatch Actions */}
                <div className="space-y-3 pt-2">
                  <a href="tel:4708479076" className="block w-full">
                    <Button size="lg" className="w-full bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90 gap-2">
                      <PhoneCall className="size-4" />
                      Call Morrow Emergency Dispatch
                    </Button>
                  </a>
                  
                  <Button asChild variant="outline" size="lg" className="w-full">
                    <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                      <Clock className="size-4" />
                      Request Service Online
                    </Link>
                  </Button>
                </div>

                <div className="pt-2 border-t border-border flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <ShieldAlert className="size-3.5 text-accent-foreground" />
                  <span>Licensed, Insured & Background-Checked Technicians</span>
                </div>
              </div>
            </Card>

            {/* Service Areas Navigation Link Card */}
            <div className="p-4 rounded-lg bg-secondary/50 border border-border text-center">
              <p className="text-xs text-muted-foreground mb-2">
                Need service in neighboring South Metro Atlanta cities?
              </p>
              <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary">
                <Link to="/service-areas/">
                  View All Service Areas
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}